import { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { login, register, resendVerification, forgotPassword, googleLogin } from '../../services/auth.js';
import styles from './AuthPages.module.css';

const VIEWS = { LOGIN: 'login', REGISTER: 'register', FORGOT: 'forgot' };

// Only show the Google button when a client id is configured.
const GOOGLE_ENABLED = Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID);

export default function LoginPage({ onLogin }) {
  const [view, setView] = useState(VIEWS.LOGIN);
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [displayName, setDisplayName] = useState('');
  const [status, setStatus]         = useState('idle'); // idle | submitting | success | error
  const [message, setMessage]       = useState('');
  const [canResendVerification, setCanResendVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState('idle'); // idle | submitting | success | error
  const [resendMessage, setResendMessage] = useState('');

  function reset(nextView) {
    setView(nextView);
    setStatus('idle');
    setMessage('');
    setPassword('');
    setCanResendVerification(false);
    setResendStatus('idle');
    setResendMessage('');
  }

  async function handleLogin(e) {
    e.preventDefault();
    setStatus('submitting');
    setCanResendVerification(false);
    setResendStatus('idle');
    setResendMessage('');
    try {
      await login(email, password);
      onLogin();
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
      setCanResendVerification(err.status === 403);
    }
  }

  async function handleResendVerification() {
    setResendStatus('submitting');
    setResendMessage('');
    try {
      await resendVerification(email);
      setStatus('idle');
      setResendStatus('success');
      setResendMessage('Verification email sent. Check your inbox; the link expires in 24 hours.');
    } catch (err) {
      setResendStatus('error');
      setResendMessage(err.message);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    if (password.length < 8) {
      setStatus('error');
      setMessage('Password must be at least 8 characters.');
      return;
    }
    setStatus('submitting');
    try {
      const data = await register(email, password, displayName);
      setStatus('success');
      setMessage(data.detail || 'Check your email to verify your account.');
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
    }
  }

  async function handleGoogle(credentialResponse) {
    setStatus('submitting');
    try {
      await googleLogin(credentialResponse.credential);
      onLogin();
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
    }
  }

  function googleBlock(label) {
    if (!GOOGLE_ENABLED) return null;
    return (
      <>
        <div className={styles.divider}><span>{label}</span></div>
        <div className={styles.googleBtn}>
          <GoogleLogin
            onSuccess={handleGoogle}
            onError={() => { setStatus('error'); setMessage('Google sign-in failed'); }}
            text={label === 'or sign up with' ? 'signup_with' : 'signin_with'}
          />
        </div>
      </>
    );
  }

  async function handleForgot(e) {
    e.preventDefault();
    setStatus('submitting');
    try {
      const data = await forgotPassword(email);
      setStatus('success');
      setMessage(data.detail || 'If that email is registered, a reset link has been sent.');
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <img src="/TDA_Logo.jpg" alt="TrackAnalysis Logo" className={styles.logo} />
        <h1 className={styles.title}>Track Day Analysis</h1>

        {view === VIEWS.LOGIN && (
          <>
            <h2 className={styles.subtitle}>Sign in</h2>
            <form onSubmit={handleLogin} className={styles.form}>
              <input className={styles.input} type="email" placeholder="Email" value={email}
                onChange={e => setEmail(e.target.value)} required autoFocus />
              <input className={styles.input} type="password" placeholder="Password" value={password}
                onChange={e => setPassword(e.target.value)} required />
              {status === 'error' && <p className={styles.error}>{message}</p>}
              {canResendVerification && resendStatus !== 'success' && (
                <button className={styles.link} type="button" onClick={handleResendVerification}
                  disabled={resendStatus === 'submitting'}>
                  {resendStatus === 'submitting' ? 'Sending…' : 'Resend verification email'}
                </button>
              )}
              {resendStatus === 'success' && <p className={styles.success}>{resendMessage}</p>}
              {resendStatus === 'error' && <p className={styles.error}>{resendMessage}</p>}
              <button className={styles.btn} type="submit" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
            {googleBlock('or sign in with')}
            <div className={styles.links}>
              <button className={styles.link} onClick={() => reset(VIEWS.FORGOT)}>Forgot password?</button>
              <button className={styles.link} onClick={() => reset(VIEWS.REGISTER)}>Create an account</button>
            </div>
          </>
        )}

        {view === VIEWS.REGISTER && (
          <>
            <h2 className={styles.subtitle}>Create account</h2>
            {status === 'success' ? (
              <>
                <p className={styles.success}>{message}</p>
                <button className={styles.btn} onClick={() => reset(VIEWS.LOGIN)}>Back to sign in</button>
              </>
            ) : (
              <form onSubmit={handleRegister} className={styles.form}>
                <input className={styles.input} type="text" placeholder="Display name (optional)"
                  value={displayName} onChange={e => setDisplayName(e.target.value)} />
                <input className={styles.input} type="email" placeholder="Email" value={email}
                  onChange={e => setEmail(e.target.value)} required />
                <input className={styles.input} type="password" placeholder="Password (min 8 characters)"
                  value={password} onChange={e => setPassword(e.target.value)} required />
                {status === 'error' && <p className={styles.error}>{message}</p>}
                <button className={styles.btn} type="submit" disabled={status === 'submitting'}>
                  {status === 'submitting' ? 'Creating account…' : 'Create account'}
                </button>
              </form>
            )}
            {status !== 'success' && googleBlock('or sign up with')}
            {status !== 'success' && (
              <div className={styles.links}>
                <button className={styles.link} onClick={() => reset(VIEWS.LOGIN)}>Already have an account?</button>
              </div>
            )}
          </>
        )}

        {view === VIEWS.FORGOT && (
          <>
            <h2 className={styles.subtitle}>Reset password</h2>
            {status === 'success' ? (
              <>
                <p className={styles.success}>{message}</p>
                <button className={styles.btn} onClick={() => reset(VIEWS.LOGIN)}>Back to sign in</button>
              </>
            ) : (
              <form onSubmit={handleForgot} className={styles.form}>
                <input className={styles.input} type="email" placeholder="Your email address" value={email}
                  onChange={e => setEmail(e.target.value)} required autoFocus />
                {status === 'error' && <p className={styles.error}>{message}</p>}
                <button className={styles.btn} type="submit" disabled={status === 'submitting'}>
                  {status === 'submitting' ? 'Sending…' : 'Send reset link'}
                </button>
              </form>
            )}
            {status !== 'success' && (
              <div className={styles.links}>
                <button className={styles.link} onClick={() => reset(VIEWS.LOGIN)}>Back to sign in</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
